import { cn } from '@neo/lib/utils'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

interface ScrollFadeListProps {
  children: ReactNode
  /** Classes for the scroll container itself — set the max height and overflow here. */
  className?: string
  /**
   * Gradient origin colour, as a Tailwind `from-*` token. Must match the surface
   * the list sits on or the fade reads as a grey band: `from-popover` inside a
   * dropdown/menu, `from-background` on a page.
   */
  fadeFrom?: string
}

/**
 * Scroll container that fades its top and bottom edges to signal there is more
 * content in that direction. Each fade only renders when the list can actually
 * scroll that way, so at the very top there is no top fade and at the very bottom
 * no bottom fade — the affordance disappears once it would be a lie.
 *
 * Mirrors the horizontal version neo already uses for overflowing filter chips
 * (`blocks/data-table-filter/components/active-filters.tsx`).
 */
export const ScrollFadeList = ({
  children,
  className,
  fadeFrom = 'from-popover',
}: ScrollFadeListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [fades, setFades] = useState({ top: false, bottom: false })

  const sync = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    // 1px tolerance on both ends: fractional scroll offsets (page zoom, HiDPI,
    // momentum scrolling) never land on an exact equality, so a strict compare
    // leaves the bottom fade stuck on after scrolling all the way down.
    const top = el.scrollTop > 1
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1
    // Bail on an unchanged pair — onScroll fires per frame and every setState
    // here would otherwise re-render the whole list mid-scroll.
    setFades((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }))
  }, [])

  useEffect(() => {
    sync()
    const el = scrollRef.current
    // happy-dom has no ResizeObserver, so guard rather than crash the test run.
    if (!el || typeof ResizeObserver === 'undefined') return
    // Observe the content wrapper, not the scroll box: the box's own height is
    // fixed by max-h, so only the content growing/shrinking changes whether the
    // list overflows at all.
    const content = el.firstElementChild
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    if (content) observer.observe(content)
    return () => observer.disconnect()
  }, [sync])

  const fadeBase =
    'pointer-events-none absolute inset-x-0 z-10 h-8 to-transparent animate-in fade-in-0'

  return (
    <div className="relative">
      {fades.top && <div className={cn(fadeBase, 'top-0 bg-gradient-to-b', fadeFrom)} />}
      <div ref={scrollRef} onScroll={sync} className={className}>
        <div>{children}</div>
      </div>
      {fades.bottom && <div className={cn(fadeBase, 'bottom-0 bg-gradient-to-t', fadeFrom)} />}
    </div>
  )
}
